import type { BlacklistedDomain, ChannelMetrics, NextScreening, PriceRecommendationMetrics, ProductMetrics, SentinelChannel, SentinelMetrics, SettingsRow } from '@/api/types'
import { delayLevel, formatDuration, notStartedSummary } from '@/features/ratescreener/nextScreening'
import { compactNumber, dateTime, percent } from '@/lib/format'
import { groupByPos, type PosScope } from '@/features/proxies/blacklistByPos'
import { isInProgress, LOW_SUCCESS_RATIO, statusOf, trendOf, type Trend } from '@/features/ratescreener/siteStatus'

export type Severity = 'critical' | 'warning' | 'info'
export type Area = 'RateScreener' | 'Sentinel' | 'Price recommendation' | 'Proxies' | 'Data sources'

/** Opens a policy table on the row where `row[field] === value`. */
export interface PolicyTarget {
  kind: 'policy'
  tableKey: string
  field: string
  value: unknown
}

/** What a click on an entry opens. */
export type EntryTarget = { kind: 'channel'; channel: ChannelMetrics } | { kind: 'sentinel'; channel: SentinelChannel } | { kind: 'blacklist'; scope?: PosScope } | PolicyTarget

/** One on/off switch shown as an icon badge: green when on, red when off. */
export interface EntryFlag {
  label: string
  enabled: boolean
  /** Names the switch in the tooltip, e.g. "Automatic sync". */
  title: string
}

export interface AttentionEntry {
  key: string
  label: string
  value: string
  note?: string
  /** Priority POS: drawn as a star next to the name, like in the sites table. */
  priority?: boolean
  /** Success ratio compared with the previous period, drawn like the arrow in the sites table. */
  trend?: { direction: Trend; reference: number | null }
  flags?: EntryFlag[]
  target?: EntryTarget
}

export interface AttentionItem {
  id: string
  area: Area
  severity: Severity
  title: string
  detail?: string
  entries: AttentionEntry[]
}

/** A POS reduced to what the disabled-POS checks need. */
export interface PosPolicy {
  name: string
  priority: string
  priorityOrder: number
  /** Automatic sync (RateScreener) or active (Sentinel). */
  automatic: boolean
  /** On-demand sync; only the RateScreener check looks at it. */
  onDemand: boolean
  target: PolicyTarget
}

export interface AttentionInput {
  rateScreener?: ProductMetrics | null
  channels?: ChannelMetrics[]
  rateScreenerPolicies?: PosPolicy[]
  nextScreening?: NextScreening
  sentinel?: SentinelMetrics
  sentinelChannels?: SentinelChannel[]
  sentinelPolicies?: PosPolicy[]
  priceReco?: PriceRecommendationMetrics
  blacklist?: BlacklistedDomain[]
  /** Data sources that failed to load: their checks cannot run, which is itself worth knowing. */
  unavailable?: string[]
}

/**
 * Cut-offs behind the popup. The ratios match the colour tones on the dashboard tiles;
 * change them here to make the popup stricter or looser.
 */
export const THRESHOLDS = {
  rateScreenerWarn: 95,
  rateScreenerCritical: 90,
  sentinelWarn: 90,
  sentinelCritical: 70,
  priceApiErrorWarn: 1,
  priceApiErrorCritical: 5,
  blacklistedIpsPerSite: 500,
}

const AREA_ORDER: Area[] = ['RateScreener', 'Sentinel', 'Price recommendation', 'Proxies', 'Data sources']
const SEVERITY_ORDER: Severity[] = ['critical', 'warning', 'info']

const plural = (count: number, singular: string, pluralForm = `${singular}s`) => `${count} ${count === 1 ? singular : pluralForm}`

// --- Policies ---------------------------------------------------------------
// Policy rows come from the settings tables; a missing switch is treated as on so it never raises a false alarm.

const text = (value: unknown) => (typeof value === 'string' ? value : '')
const isOn = (value: unknown) => value !== false

function toPolicy(row: SettingsRow, name: string, tableKey: string, field: string, automatic: unknown, onDemand: unknown): PosPolicy {
  return {
    name,
    priority: text(row.priority),
    priorityOrder: Number(row.priorityOrder) || 0,
    automatic: isOn(automatic),
    onDemand: isOn(onDemand),
    target: { kind: 'policy', tableKey, field, value: row[field] },
  }
}

/** RateScreener channel and booking engine policies as one list; undefined while neither table has loaded. */
export function rateScreenerPolicies(channels?: SettingsRow[], bookingEngines?: SettingsRow[]): PosPolicy[] | undefined {
  if (!channels && !bookingEngines) return undefined
  return [
    ...(channels ?? []).map((r) => toPolicy(r, text(r.channelName), 'channel-policies', 'channelId', r.isAutomaticScrapingActive, r.isOnDemandScrapingActive)),
    ...(bookingEngines ?? []).map((r) => toPolicy(r, text(r.bookingEngine), 'booking-engine-policies', 'bookingEngine', r.isAutomaticScrapingActive, r.isOnDemandScrapingActive)),
  ]
}

export function sentinelPolicies(rows?: SettingsRow[]): PosPolicy[] | undefined {
  return rows?.map((r) => toPolicy(r, text(r.channelName), 'sentinel-channel-policies', 'channelId', r.isActive, r.isOnDemandActive))
}

const isPriority = (channel: ChannelMetrics) => channel.priority === 'Prio'

/** "was 73.0%" when the ratio moved against the previous period; nothing when it is steady or unknown. */
function trendNote(channel: ChannelMetrics, trend: Trend): string | undefined {
  return (trend === 'down' || trend === 'up') && channel.referenceSuccessRatio !== null ? `was ${percent(channel.referenceSuccessRatio, 1)}` : undefined
}

/** Builds the list of things worth a look, most severe first. Pure: all data comes in through `input`. */
export function buildAttention(input: AttentionInput): AttentionItem[] {
  const items: AttentionItem[] = []
  const push = (item: Omit<AttentionItem, 'entries'> & { entries?: AttentionEntry[] }) => items.push({ entries: [], ...item })

  const { rateScreener, channels, nextScreening, sentinel, sentinelChannels, priceReco, blacklist, unavailable } = input

  // --- RateScreener -------------------------------------------------------
  // A late wave comes first: it means hotels are not being screened at all.
  if (nextScreening) {
    const level = delayLevel(nextScreening)
    if (level) {
      const shown = nextScreening.notRunningHotelIds.slice(0, 20)
      push({
        id: 'rs-delay',
        area: 'RateScreener',
        severity: level,
        title: `RateScreener is running ${formatDuration(nextScreening.delayTime)} late`,
        detail:
          `The ${dateTime(nextScreening.scheduledAt, 'HH:mm')} wave ${notStartedSummary(nextScreening)}.` +
          (shown.length < nextScreening.notRunningHotelIds.length ? ` The first ${shown.length} hotels are listed.` : ''),
        entries: shown.map((id) => ({ key: String(id), label: `Hotel ${id}`, value: '' })),
      })
    }
  }

  if (rateScreener && rateScreener.successRatio < THRESHOLDS.rateScreenerWarn) {
    push({
      id: 'rs-success',
      area: 'RateScreener',
      severity: rateScreener.successRatio < THRESHOLDS.rateScreenerCritical ? 'critical' : 'warning',
      title: `RateScreener success ratio is ${percent(rateScreener.successRatio, 1)}`,
      detail: `Target ${THRESHOLDS.rateScreenerWarn}%. Including unfinished synchronisations it is ${percent(rateScreener.successRatioWithUnfinished, 1)}, with ${percent(rateScreener.finishedRate, 0)} finished.`,
    })
  }

  if (channels) {
    const low = channels.filter((c) => statusOf(c) === 'warning').sort((a, b) => a.successRatio - b.successRatio)
    if (low.length > 0) {
      const priority = low.filter(isPriority).length
      const dropped = low.filter((c) => trendOf(c) === 'down').length
      push({
        id: 'rs-low',
        area: 'RateScreener',
        // A priority site below the cut-off is escalated: those are the ones customers notice first.
        severity: priority > 0 ? 'critical' : 'warning',
        title: `${plural(low.length, 'POS', 'POSs')} with low success (under ${LOW_SUCCESS_RATIO}%)`,
        detail: [priority > 0 ? `${plural(priority, 'priority site')} included.` : '', dropped > 0 ? `${dropped} lower than 36 hours ago.` : ''].filter(Boolean).join(' ') || undefined,
        entries: low.map((c) => {
          const direction = trendOf(c)
          return {
            key: `${c.channelId}:${c.name}`,
            label: c.name,
            value: percent(c.successRatio, 1),
            note: trendNote(c, direction),
            priority: isPriority(c),
            trend: { direction, reference: c.referenceSuccessRatio },
            target: { kind: 'channel', channel: c },
          }
        }),
      })
    }

    const declining = channels
      .filter((c) => trendOf(c) === 'down')
      .sort((a, b) => a.successRatio - a.referenceSuccessRatio! - (b.successRatio - b.referenceSuccessRatio!))
    if (declining.length > 0) {
      push({
        id: 'rs-declining',
        area: 'RateScreener',
        severity: 'warning',
        title: `${plural(declining.length, 'POS', 'POSs')} with a lower success ratio than 36 hours ago`,
        detail: 'Only finished synchronisations are compared, so a running one does not count as a drop.',
        entries: declining.map((c) => ({
          key: `${c.channelId}:${c.name}`,
          label: c.name,
          value: percent(c.successRatio, 1),
          note: `was ${percent(c.referenceSuccessRatio, 1)}`,
          priority: isPriority(c),
          trend: { direction: 'down', reference: c.referenceSuccessRatio },
          target: { kind: 'channel', channel: c },
        })),
      })
    }

    const running = channels.filter(isInProgress).sort((a, b) => a.progress - b.progress)
    if (running.length > 0) {
      push({
        id: 'rs-in-progress',
        area: 'RateScreener',
        severity: 'info',
        title: `${plural(running.length, 'POS', 'POSs')} still in progress`,
        detail: 'Their ratios are not final until the synchronisation finishes.',
        entries: running.map((c) => ({
          key: `${c.channelId}:${c.name}`,
          label: c.name,
          value: `${c.progress.toFixed(0)}%`,
          note: 'done',
          priority: isPriority(c),
          target: { kind: 'channel', channel: c },
        })),
      })
    }
  }

  // A POS is disabled when automatic or on-demand synchronisation is switched off. Only priority POSs are listed here.
  const disabledPriority = (input.rateScreenerPolicies ?? [])
    .filter((p) => p.priority === 'Prio' && (!p.automatic || !p.onDemand))
    .sort((a, b) => (a.priorityOrder || Infinity) - (b.priorityOrder || Infinity) || a.name.localeCompare(b.name))
  if (disabledPriority.length > 0) {
    push({
      id: 'rs-disabled-priority',
      area: 'RateScreener',
      severity: 'warning',
      title: `${plural(disabledPriority.length, 'priority POS', 'priority POSs')} with synchronisation disabled`,
      detail: 'Automatic or on-demand synchronisation is switched off. The number is the priority order.',
      entries: disabledPriority.map((p) => ({
        key: `${p.target.tableKey}:${p.name}`,
        label: p.name,
        value: p.priorityOrder > 0 ? `#${p.priorityOrder}` : '–',
        priority: true,
        flags: [
          { label: 'Automatic', enabled: p.automatic, title: 'Automatic synchronisation' },
          { label: 'On demand', enabled: p.onDemand, title: 'On-demand synchronisation' },
        ],
        target: p.target,
      })),
    })
  }

  // --- Sentinel -----------------------------------------------------------
  if (sentinel && sentinel.totalRequests > 0 && sentinel.successRatio < THRESHOLDS.sentinelWarn) {
    push({
      id: 'sentinel-success',
      area: 'Sentinel',
      severity: sentinel.successRatio < THRESHOLDS.sentinelCritical ? 'critical' : 'warning',
      title: `Sentinel success ratio is ${percent(sentinel.successRatio, 1)}`,
      detail: `${compactNumber(sentinel.totalRequests)} requests for ${plural(sentinel.customers, 'customer')} in the last 24 hours. Target ${THRESHOLDS.sentinelWarn}%.`,
    })
  }

  if (sentinelChannels) {
    const bad = sentinelChannels.filter((c) => c.successRatio < THRESHOLDS.sentinelWarn).sort((a, b) => a.successRatio - b.successRatio)
    if (bad.length > 0) {
      push({
        id: 'sentinel-sites',
        area: 'Sentinel',
        severity: bad.some((c) => c.successRatio < THRESHOLDS.sentinelCritical) ? 'critical' : 'warning',
        title: `${plural(bad.length, 'Sentinel site')} with a bad success ratio (under ${THRESHOLDS.sentinelWarn}%)`,
        entries: bad.map((c) => ({
          key: String(c.channelId),
          label: c.name,
          value: percent(c.successRatio, 1),
          note: `${compactNumber(c.totalRequests)} requests`,
          target: { kind: 'sentinel', channel: c },
        })),
      })
    }
  }

  // A Sentinel POS is disabled when it is not active (on-demand does not count), whatever its priority.
  // Most are deliberate, hence "to watch".
  const disabledSentinel = (input.sentinelPolicies ?? [])
    .filter((p) => !p.automatic)
    .sort((a, b) => Number(b.priority === 'Prio') - Number(a.priority === 'Prio') || a.name.localeCompare(b.name))
  if (disabledSentinel.length > 0) {
    push({
      id: 'sentinel-disabled',
      area: 'Sentinel',
      severity: 'info',
      title: `${plural(disabledSentinel.length, 'Sentinel POS', 'Sentinel POSs')} disabled`,
      detail: 'Switched off (not active) in the Sentinel channel policy.',
      entries: disabledSentinel.map((p) => ({
        key: p.name,
        label: p.name,
        // The star carries "priority"; only the unusual "Blacklisted" level is spelled out.
        value: p.priority === 'Blacklisted' ? 'Blacklisted' : '',
        note: 'not active',
        priority: p.priority === 'Prio',
        target: p.target,
      })),
    })
  }

  // --- Price recommendation: the basics only ------------------------------
  if (priceReco) {
    if (priceReco.healthy === false) {
      push({
        id: 'price-down',
        area: 'Price recommendation',
        severity: 'critical',
        title: 'Price recommendation API is down',
        detail: 'The health probe of the recommendation API is failing.',
      })
    }

    if (priceReco.metricsAvailable && priceReco.errorRate > THRESHOLDS.priceApiErrorWarn) {
      push({
        id: 'price-errors',
        area: 'Price recommendation',
        severity: priceReco.errorRate > THRESHOLDS.priceApiErrorCritical ? 'critical' : 'warning',
        title: `Price recommendation API error rate is ${percent(priceReco.errorRate, 2)}`,
        detail: `${compactNumber(priceReco.requestErrors)} of ${compactNumber(priceReco.requests)} requests failed in the last ${priceReco.windowHours} hours.`,
      })
    }

    if (priceReco.metricsAvailable && priceReco.recommendationsGenerated === 0) {
      push({
        id: 'price-none',
        area: 'Price recommendation',
        severity: 'warning',
        title: `No price recommendation generated in the last ${priceReco.windowHours} hours`,
        detail: `${compactNumber(priceReco.requests)} requests reached the API in that time.`,
      })
    }
  }

  // --- Proxies ------------------------------------------------------------
  if (blacklist) {
    // Counted per POS: a POS with several domains adds them up.
    const heavy = groupByPos(blacklist).filter((g) => g.count > THRESHOLDS.blacklistedIpsPerSite)
    if (heavy.length > 0) {
      push({
        id: 'blacklist-heavy',
        area: 'Proxies',
        severity: 'critical',
        title: `${plural(heavy.length, 'POS', 'POSs')} with more than ${THRESHOLDS.blacklistedIpsPerSite} blacklisted IPs`,
        entries: heavy.map((g) => ({
          key: g.key,
          label: g.label,
          value: `${g.count} IPs`,
          note: g.domains.join(', '),
          target: { kind: 'blacklist', scope: { label: g.label, pos: g.pos } },
        })),
      })
    }
  }

  // --- Data sources -------------------------------------------------------
  if (unavailable && unavailable.length > 0) {
    push({
      id: 'unavailable',
      area: 'Data sources',
      severity: 'warning',
      title: `${plural(unavailable.length, 'data source')} could not be loaded`,
      detail: 'The checks that need them are skipped, so the list above may be incomplete.',
      entries: unavailable.map((name) => ({ key: name, label: name, value: 'unavailable' })),
    })
  }

  return items.sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity) || AREA_ORDER.indexOf(a.area) - AREA_ORDER.indexOf(b.area))
}

export function summarise(items: AttentionItem[]): Record<Severity, number> {
  const counts: Record<Severity, number> = { critical: 0, warning: 0, info: 0 }
  for (const item of items) counts[item.severity] += 1
  return counts
}

export { AREA_ORDER }
